import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  type FlexProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { SeeTheListButton } from "./SeeTheListButton";
import { InPageDetails, useIsTouchScreen, useTouchDetails } from "./TouchDetails";

type BarProps = {
  label: string;
  width: number | null;
  shown: string;
  details: string[];
  link: string | null;
  linkLabel?: string;
  testId: string;
};

export const OverviewBar = (props: BarProps) =>
  useIsTouchScreen() ? <TouchBar {...props} /> : <HoverBar {...props} />;

const HoverBar = (props: BarProps) => {
  const navigate = useNavigate();
  const { label, width, shown, link, testId } = props;
  return (
    <Popover trigger="hover" placement="bottom-start" isLazy openDelay={150}>
      <PopoverTrigger>
        <BarButton
          label={label}
          width={width}
          shown={shown}
          testId={testId}
          onClick={link ? () => navigate(link) : undefined}
          data-testid={testId}
        />
      </PopoverTrigger>
      <PopoverContent maxW="xs" bg="smBackground" borderColor="smBorder" data-testid={`${testId}-details`}>
        <PopoverArrow bg="smBackground" />
        <PopoverBody fontSize="sm">
          <BarDetails {...props} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const TouchBar = (props: BarProps) => {
  const { regionRef, openKey, triggerProps, detailsId } = useTouchDetails<string>();
  const { label, width, shown, testId } = props;
  return (
    <Box ref={regionRef}>
      <BarButton
        label={label}
        width={width}
        shown={shown}
        testId={testId}
        {...triggerProps(testId)}
        _expanded={{ bg: "smPrimarySofter" }}
        data-testid={testId}
      />
      {openKey === testId && (
        <InPageDetails id={detailsId} mt="1" mb="2" data-testid={`${testId}-details`}>
          <BarDetails {...props} />
        </InPageDetails>
      )}
    </Box>
  );
};

const BarButton = forwardRef<HTMLButtonElement, Omit<FlexProps, "width"> & Pick<BarProps, "label" | "width" | "shown" | "testId">>(
  function BarButton({ label, width, shown, testId, ...rest }, ref) {
    return (
      <Flex
        ref={ref}
        as="button"
        type="button"
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        gap={{ base: "1", md: "3" }}
        w="full"
        textAlign="left"
        borderRadius="sm"
        px="1"
        py="0.5"
        _hover={{ bg: "smPrimarySofter" }}
        _focusVisible={{ boxShadow: "outline" }}
        {...rest}
      >
        <Text fontSize="sm" color="smText" w={{ base: "full", md: "12rem" }} flexShrink={0} overflowWrap="anywhere">
          {label}
        </Text>
        <Flex flex="1" minW="0" align="center" gap="3">
          <Box flex="1" minW="0">
            {width != null && (
              <Box h="3" w={`${Math.max(width, 1)}%`} bg="smChartSeries1" borderRadius="sm" data-testid={`${testId}-bar`} />
            )}
          </Box>
          <Text fontSize="xs" color="smMuted" flexShrink={0}>
            {shown}
          </Text>
        </Flex>
      </Flex>
    );
  },
);

const BarDetails = ({ label, details, link, linkLabel }: BarProps) => (
  <Stack spacing="1">
    <Text fontWeight="semibold" color="smText">
      {label}
    </Text>
    {details.map((line) => (
      <Text key={line} color="smMuted">
        {line}
      </Text>
    ))}
    {link && <SeeTheListButton to={link} label={linkLabel} />}
  </Stack>
);
